import {
  ActionLabel,
  Card,
  CardContent,
  Inline,
  StoryCard,
  StoryCardMedia,
} from "@generalapplications/ui";

import { PostByline } from "./PostByline";
import { ResponsiveImage } from "@/app/common/components/composite/media/ResponsiveImage";
import { StatusBadge } from "@/app/common/components/composite/feedback/StatusBadge";
import { Tag } from "@/app/common/components/composite/feedback/Tag";
import type { BlogPostSummary } from "@/content/blog";
import { routes } from "@/app/common/functions/routes/routes";

const statusNote = {
  idea: "Editorial idea — hidden in production.",
  draft: "Draft preview — hidden in production.",
  ready: "Ready for release — intended target date.",
} as const;

type PostCardProps = {
  post: BlogPostSummary;
};

type PostStoryProps = PostCardProps & {
  size: "sm" | "md" | "lg";
  level: 2 | 3;
  featured?: boolean;
};

/** A post as the library's story: picture, label, headline, standfirst, byline. */
function PostStory({ post, size, level, featured = false }: PostStoryProps) {
  const status = post.status !== "published" && (
    <StatusBadge status={post.status} title={statusNote[post.status]} />
  );

  return (
    <StoryCard
      href={routes.blog.detail(post.slug)}
      title={post.title}
      size={size}
      level={level}
      eyebrow={
        featured || status ? (
          <>
            {featured && "Featured article "}
            {status}
          </>
        ) : undefined
      }
      description={post.summary}
      meta={<PostByline post={post} />}
    >
      {post.image && (
        <StoryCardMedia>
          <ResponsiveImage
            src={post.image}
            width={1200}
            height={630}
            ratio="16:9"
          />
        </StoryCardMedia>
      )}
    </StoryCard>
  );
}

type PostTagsProps = {
  tags: readonly string[];
};

function PostTags({ tags }: PostTagsProps) {
  return (
    <Inline wrap gap="xs">
      {tags.map((tag) => (
        <Tag key={tag}>{tag}</Tag>
      ))}
    </Inline>
  );
}

export type FeaturedArticleCardProps = PostCardProps;

/** The newest post, given the width of the index. */
export function FeaturedArticleCard({ post }: FeaturedArticleCardProps) {
  return (
    <Card radius="xxl" padding="lg" interactive>
      <CardContent gap="lg">
        <PostStory post={post} size="lg" level={2} featured />
        <PostTags tags={post.tags} />
      </CardContent>
    </Card>
  );
}

export type ArticleCardProps = PostCardProps;

/** A post in the index grid. */
export function ArticleCard({ post }: ArticleCardProps) {
  return (
    <Card radius="xxl" padding="md" height="full" interactive>
      <CardContent grow gap="lg">
        <PostStory post={post} size="md" level={3} />
        <PostTags tags={post.tags} />
      </CardContent>
    </Card>
  );
}

export type RelatedArticleCardProps = PostCardProps;

/** A post suggested under another, or under a service it writes about. */
export function RelatedArticleCard({ post }: RelatedArticleCardProps) {
  return (
    <Card radius="xl" padding="md" height="full" interactive>
      <CardContent grow gap="md">
        <PostStory post={post} size="sm" level={3} />
        <ActionLabel>Read Article</ActionLabel>
      </CardContent>
    </Card>
  );
}
